import { JSONSchema7 } from 'json-schema';
import { types as commonTypes } from '@sitebuilder/common';
import DataContextView from './DataContext';

declare module 'json-schema' {
    export interface JSONSchema7 {
        enumNames?: Array<string | number>;
    }
}

interface DynamicProperty {
    propertyName: string;
    propertyType?: string;
    propertyBooleanValue?: string;
    propertySingleValue?: string;
    propertyArrayValue: PropertyArrayValue[];
}

export interface PropertyArrayValue {
    propertyValue?: string;
}

export interface DynamicSchemaWidgetProperties {
    dynamicProperties?: DynamicProperty[];
}

// Function to generate schema for dynamic events
const DynamicProperties = (): JSONSchema7 => ({
    type: 'object',
    properties: {
        propertyType: {
            type: 'string',
            title: 'Custom property type',
            enum: ['', 'Boolean', 'Single value', 'Multiple values'],
            default: '',
        },
    },
    dependencies: {
        propertyType: {
            oneOf: [
                {
                    properties: {
                        propertyType: { enum: [''] },
                    },
                },
                {
                    properties: {
                        propertyType: { enum: ['Boolean'] },
                        propertyBooleanValue: {
                            type: 'boolean',
                            title: 'Property value',
                            default: true,
                        },
                        propertyName: {
                            type: 'string',
                            title: 'Property name',
                        },
                    },
                },
                {
                    properties: {
                        propertyType: { enum: ['Single value'] },
                        propertySingleValue: {
                            type: 'string',
                            title: 'Property value',
                            default: '',
                        },
                        propertyName: {
                            type: 'string',
                            title: 'Property name',
                        },
                    },
                },
                {
                    properties: {
                        propertyType: { enum: ['Multiple values'] },
                        propertyArrayValue: {
                            type: 'array',
                            title: 'Property values',
                            items: {
                                type: 'object',
                                properties: {
                                    propertyValue: {
                                        type: 'string',
                                        title: 'Property value',
                                    },
                                },
                            },
                        },
                        propertyName: {
                            type: 'string',
                            title: 'Property name',
                        },
                    },
                },
            ],
        },
    },
});

const schema: JSONSchema7 = {
    title: 'Data Context',
    type: 'object',
    required: [],
    properties: {
        dynamicProperties: {
            type: 'array',
            title: 'Add properties with Custom Configuration',
            items: DynamicProperties(),
        },
        generatedProperties: {
            type: 'object',
            title: 'Generated Properties',
            properties: {},
        },
        dataSourceType: {
            type: 'string',
            description: 'Data Source Type',
            enum: ['', 'GraphQL'],
            default: '',
        },
        dataSource: {
            type: 'string',
            description: 'data Source',
            default: '',
        },
        query: {
            type: 'string',
            description: 'A graphql query',
            default: '',
        },
        queryVariables: {
            type: 'string',
            description: 'Query variables',
            default: '',
        },
        transform: {
            type: 'string',
            description: 'A transform function to transform the graphql query results',
            default: '',
        },
        isLazyLoad: {
            type: 'boolean',
            description: 'Lazy load the Query',
            default: false,
        },
    },
};

const groups = [
    {
        'Dynamic Properties': [
            'dynamicProperties',
            'propertyBooleanValue',
            'propertySingleValue',
            'propertyArrayValue',
        ],
        'Generated Properties': ['generatedProperties'],
        'Query Properties': [
            'dataSourceType',
            'dataSource',
            'query',
            'queryVariables',
            'transform',
            'isLazyLoad',
        ],
    },
];

const DataContext: commonTypes.IWidget = {
    // without default
    schema,
    uiSchema: {
        'ui:groups': groups,
        dynamicProperties: {
            items: {
                propertyName: { 'ui:widget': 'CustomTextInput' },
                propertyType: { 'ui:widget': 'CustomSelect' },
                propertyBooleanValue: { 'ui:widget': 'CustomCheckbox' },
                propertySingleValue: { 'ui:widget': 'CustomTextInput' },
                propertyArrayValue: {
                    items: {
                        propertyValue: {
                            'ui:widget': 'CustomTextInput',
                        },
                    },
                },
            },
        },
        dataSourceType: {
            'ui:widget': 'CustomSelect',
        },
        dataSource: {
            'ui:widget': 'CustomTextInput',
        },
        query: {
            'ui:widget': 'JSEditor',
        },
        queryVariables: {
            'ui:widget': 'JSEditor',
        },
        transform: {
            'ui:widget': 'JSEditor',
        },
        isLazyLoad: {
            'ui:widget': 'CustomCheckbox',
        },
    },
    templateView: DataContextView,
    id: 'DataContext',
    group: 'data',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default DataContext;
